import React, { useState, useEffect, useMemo } from 'react';
import { sheetAPI } from '../../services/api';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import CollapsibleFilterPanel, { FilterField, filterInputProps } from '../../components/common/CollapsibleFilterPanel';
import { formatDateTime, toDayStart, toDayEnd } from '../../utils/dateTime';
import TableScroll, { tableBaseStyle } from '../../components/common/TableScroll';

const defaultFilters = {
  searchTerm: '',
  creationDateFrom: '',
  creationDateTo: '',
  sortDirection: 'DESC',
};

const isGoogleAuthError = (message = '') => {
  const lower = String(message).toLowerCase();
  return lower.includes('invalid_grant')
    || lower.includes('authorization expired')
    || lower.includes('google authorization')
    || lower.includes('oauth2.googleapis.com/token');
};

const GOOGLE_AUTH_URL = `${process.env.REACT_APP_API_URL || 'https://zmaonline.in/api'}/google/drive/auth`;

const showGoogleAuthToast = (message) => {
  toast.error(message || 'Google authorization expired. Please reconnect Google Drive & Sheets.', {
    duration: 12000,
  });
  if (window.confirm('Google authorization expired. Open the re-connect page now?')) {
    window.open(GOOGLE_AUTH_URL, '_blank', 'noopener,noreferrer');
  }
};

const Sheets = () => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleAuthorized, setGoogleAuthorized] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterForm, setFilterForm] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    sheetId: null,
  });

  const fetchSheets = async () => {
    try {
      setLoading(true);
      const response = await sheetAPI.getSheets();
      if (response.data.success) {
        setSheets(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to fetch sheets');
      }
    } catch (error) {
      console.error('Error fetching sheets:', error);
      toast.error('Failed to load sheets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets();
    api.get('/google/drive/status')
      .then((res) => setGoogleAuthorized(Boolean(res.data?.data?.authorized)))
      .catch(() => setGoogleAuthorized(false));
  }, []);

  const filteredSheets = useMemo(() => {
    const term = appliedFilters.searchTerm.trim().toLowerCase();
    const from = toDayStart(appliedFilters.creationDateFrom);
    const to = toDayEnd(appliedFilters.creationDateTo);

    let result = sheets.filter((sheet) => {
      const matchesSearch = !term
        || (sheet.fileName || '').toLowerCase().includes(term)
        || (sheet.spreadsheetId || '').toLowerCase().includes(term);

      if (!matchesSearch) return false;

      if (!from && !to) return true;

      const created = sheet.creationDate ? new Date(sheet.creationDate) : null;
      if (!created || Number.isNaN(created.getTime())) return false;
      if (from && created < from) return false;
      if (to && created > to) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      const aTime = a.creationDate ? new Date(a.creationDate).getTime() : 0;
      const bTime = b.creationDate ? new Date(b.creationDate).getTime() : 0;
      return appliedFilters.sortDirection === 'ASC' ? aTime - bTime : bTime - aTime;
    });

    return result;
  }, [sheets, appliedFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.searchTerm.trim()) count += 1;
    if (appliedFilters.creationDateFrom) count += 1;
    if (appliedFilters.creationDateTo) count += 1;
    if (appliedFilters.sortDirection !== 'DESC') count += 1;
    return count;
  }, [appliedFilters]);

  const activeFilters = useMemo(() => {
    const chips = [];
    if (appliedFilters.searchTerm.trim()) {
      chips.push({
        key: 'search',
        label: 'Search',
        value: appliedFilters.searchTerm.trim(),
        onClear: () => {
          setFilterForm((prev) => ({ ...prev, searchTerm: '' }));
          setAppliedFilters((prev) => ({ ...prev, searchTerm: '' }));
        },
      });
    }
    if (appliedFilters.creationDateFrom) {
      chips.push({
        key: 'from',
        label: 'From',
        value: appliedFilters.creationDateFrom,
        onClear: () => {
          setFilterForm((prev) => ({ ...prev, creationDateFrom: '' }));
          setAppliedFilters((prev) => ({ ...prev, creationDateFrom: '' }));
        },
      });
    }
    if (appliedFilters.creationDateTo) {
      chips.push({
        key: 'to',
        label: 'To',
        value: appliedFilters.creationDateTo,
        onClear: () => {
          setFilterForm((prev) => ({ ...prev, creationDateTo: '' }));
          setAppliedFilters((prev) => ({ ...prev, creationDateTo: '' }));
        },
      });
    }
    if (appliedFilters.sortDirection !== 'DESC') {
      chips.push({
        key: 'sort',
        label: 'Sort',
        value: 'Oldest first',
        onClear: () => {
          setFilterForm((prev) => ({ ...prev, sortDirection: 'DESC' }));
          setAppliedFilters((prev) => ({ ...prev, sortDirection: 'DESC' }));
        },
      });
    }
    return chips;
  }, [appliedFilters]);

  const handleFilterChange = (field, value) => {
    setFilterForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filterForm });
  };

  const handleResetFilters = () => {
    setFilterForm(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const openDeleteModal = (spreadsheetId) => {
    setDeleteModal({ open: true, sheetId: spreadsheetId });
  };

  const confirmDelete = async () => {
    try {
      setDeletingId(deleteModal.sheetId);

      const response = await sheetAPI.deleteSheet(deleteModal.sheetId);

      if (response.data.success) {
        toast.success(response.data.message || 'Sheet deleted successfully');
        fetchSheets();
      } else {
        toast.error(response.data.message || 'Failed to delete sheet');
      }
    } catch (error) {
      console.error('Error deleting sheet:', error);
      const message = error.response?.data?.message || 'Failed to delete sheet';
      if (isGoogleAuthError(message)) {
        showGoogleAuthToast(message);
      } else {
        toast.error(message);
      }
    } finally {
      setDeletingId(null);
      setDeleteModal({ open: false, sheetId: null });
    }
  };

  const handleSync = async (spreadsheetId) => {
    try {
      setSyncingId(spreadsheetId);
      const response = await sheetAPI.syncSheet(spreadsheetId);
      if (response.data.success) {
        toast.success(response.data.message || 'Sheet synced successfully');
      } else {
        toast.error(response.data.message || 'Failed to sync sheet');
      }
    } catch (error) {
      console.error('Error syncing sheet:', error);
      toast.error('Failed to sync sheet');
    } finally {
      setSyncingId(null);
    }
  };

  const renderSheetActions = (sheet) => (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(sheet.url, '_blank')}
        className="text-blue-600 border-blue-200 hover:bg-blue-50 flex items-center gap-2"
      >
        <Icon name="ExternalLink" size={16} />
        Open
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleSync(sheet.spreadsheetId)}
        disabled={syncingId === sheet.spreadsheetId}
        className="text-primary hover:bg-primary/10 flex items-center gap-2"
      >
        <Icon
          name="RefreshCw"
          size={16}
          className={syncingId === sheet.spreadsheetId ? 'animate-spin' : ''}
        />
        {syncingId === sheet.spreadsheetId ? 'Syncing...' : 'Sync'}
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => openDeleteModal(sheet.spreadsheetId)}
        disabled={deletingId === sheet.spreadsheetId}
        className="text-red-600 hover:bg-red-50 flex items-center gap-2"
      >
        <Icon
          name="Trash2"
          size={16}
          className={deletingId === sheet.spreadsheetId ? 'animate-pulse' : ''}
        />
        {deletingId === sheet.spreadsheetId ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
      <div className="page-container p-6">
        <div className="page-header flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Google Sheets</h1>
            <p className="text-gray-600">Manage and sync generated order sheets</p>
          </div>
          <Button
              variant="outline"
              onClick={fetchSheets}
              disabled={loading}
              className="flex items-center gap-2"
          >
            <Icon name="RefreshCw" size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>

        {!googleAuthorized && (
          <div
            style={{
              marginBottom: '16px',
              padding: '14px 16px',
              borderRadius: '12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <div style={{ fontWeight: 700, color: '#991b1b' }}>
              Google Drive & Sheets not connected
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: '#b91c1c' }}>
              Sheet upload, sync, and delete require Google authorization. Open the link below, sign in with the Google account that owns your sheets folder, and approve access.
            </p>
            <Button
              variant="primary"
              onClick={() => window.open(GOOGLE_AUTH_URL, '_blank', 'noopener,noreferrer')}
              style={{ alignSelf: 'flex-start' }}
            >
              Connect Google Account
            </Button>
          </div>
        )}

        <div className="mb-4">
          <CollapsibleFilterPanel
            title="Search & Filters"
            defaultOpen={false}
            activeCount={activeFilterCount}
            activeFilters={activeFilters}
            onReset={handleResetFilters}
            onSearch={handleApplyFilters}
          >
            <FilterField label="Search">
              <input
                type="text"
                placeholder="File name or ID..."
                value={filterForm.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                {...filterInputProps}
              />
            </FilterField>
            <FilterField label="Created from">
              <input
                type="date"
                value={filterForm.creationDateFrom}
                onChange={(e) => handleFilterChange('creationDateFrom', e.target.value)}
                {...filterInputProps}
              />
            </FilterField>
            <FilterField label="Created to">
              <input
                type="date"
                value={filterForm.creationDateTo}
                onChange={(e) => handleFilterChange('creationDateTo', e.target.value)}
                {...filterInputProps}
              />
            </FilterField>
            <FilterField label="Sort">
              <select
                value={filterForm.sortDirection}
                onChange={(e) => handleFilterChange('sortDirection', e.target.value)}
                {...filterInputProps}
              >
                <option value="DESC">Newest first</option>
                <option value="ASC">Oldest first</option>
              </select>
            </FilterField>
          </CollapsibleFilterPanel>
          <p className="text-xs text-gray-500 mt-2 px-1">
            Showing {filteredSheets.length} of {sheets.length} sheets
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {loading ? (
            <div className="px-6 py-10 text-center text-gray-500">
              <div className="flex flex-col items-center gap-2">
                <Icon name="Loader" size={24} className="animate-spin text-primary" />
                Loading sheets...
              </div>
            </div>
          ) : filteredSheets.length === 0 ? (
            <div className="px-6 py-10 text-center text-gray-500">
              No sheets found
            </div>
          ) : (
            <>
              <div className="mobile-only-list">
                <div className="mobile-card-list" style={{ padding: '12px' }}>
                  {filteredSheets.map((sheet) => (
                    <div key={sheet.spreadsheetId} className="mobile-card">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-50 rounded-lg text-green-600">
                          <Icon name="FileText" size={20} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="font-bold text-gray-800" style={{ wordBreak: 'break-word' }}>
                            {sheet.fileName}
                          </div>
                          <div className="text-gray-600 text-sm" style={{ marginTop: '4px' }}>
                            {formatDateTime(sheet.creationDate)}
                          </div>
                        </div>
                      </div>
                      <code
                        style={{
                          display: 'block',
                          marginTop: '10px',
                          background: '#f1f5f9',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          color: '#475569',
                          wordBreak: 'break-all',
                        }}
                      >
                        {sheet.spreadsheetId}
                      </code>
                      <div className="mobile-card-actions">
                        {renderSheetActions(sheet)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="desktop-only-table">
                <TableScroll>
                  <table style={{ ...tableBaseStyle, minWidth: '920px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '16px 24px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>File Name</th>
                        <th style={{ padding: '16px 24px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Creation Date</th>
                        <th style={{ padding: '16px 24px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Spreadsheet ID</th>
                        <th style={{ padding: '16px 24px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSheets.map((sheet) => (
                        <tr key={sheet.spreadsheetId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '16px 24px' }}>
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-50 rounded-lg text-green-600">
                                <Icon name="FileText" size={20} />
                              </div>
                              <span className="font-medium text-gray-800">{sheet.fileName}</span>
                            </div>
                          </td>
                          <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                            {formatDateTime(sheet.creationDate)}
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <code style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', color: '#4b5563' }}>
                              {sheet.spreadsheetId}
                            </code>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <div className="flex items-center gap-3 flex-wrap">
                              {renderSheetActions(sheet)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </div>
            </>
          )}
        </div>

        {deleteModal.open && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Delete Sheet</h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                    Are you sure you want to delete? This action cannot be undone.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <Button
                      variant="secondary"
                      onClick={() => setDeleteModal({ open: false, sheetId: null })}
                  >
                    Cancel
                  </Button>
                  <Button
                      onClick={confirmDelete}
                      style={{ background: '#ef4444', borderColor: '#ef4444' }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default Sheets;
